import React, { Component } from 'react';
import _ from 'lodash';
import config from '../config';
import {
    Link,
    Redirect
} from "react-router-dom";
import { Field, Formik } from "formik";
import { AuthContext } from "../context/auth";
import fetcher from "../context/fetcher";
import Timeline, {
    TimelineHeaders,
    SidebarHeader,
    DateHeader
} from 'react-calendar-timeline';
import 'react-calendar-timeline/lib/Timeline.css';
import moment from 'moment';

const EVENT_COLOR = "#4e73df";
const OFFER_COLOR = "#703c42";
const NOTIFICATION_COLOR = "#42703c";
const REMOTE_GAME_MODE_COLOR = "#1c7c54";
const REMOTE_TOURNAMENT_COLOR = "#9c6644";
const REMOTE_NOTIFICATION_COLOR = "#6c757d";
const EXTERNAL_SOURCE_STORAGE_KEY = "calendar-external-source-url";
const EXTERNAL_SOURCE_TIMEOUT_MS = 5000;
const TIMELINE_MIN_ZOOM_MS = 60 * 60 * 1000;
const TIMELINE_MAX_ZOOM_MS = Math.round(5 * 365.24 * 24 * 60 * 60 * 1000);
const TIMELINE_WHEEL_ZOOM_DIVISOR = 2400;

function parseTimelineDate(value) {
    if (!value) {
        return null;
    }

    const parsed = moment(value);
    if (!parsed.isValid()) {
        return null;
    }

    return parsed;
}

function getStoredExternalSourceUrl() {
    try {
        return localStorage.getItem(EXTERNAL_SOURCE_STORAGE_KEY) || "";
    } catch (error) {
        return "";
    }
}

function setStoredExternalSourceUrl(value) {
    try {
        localStorage.setItem(EXTERNAL_SOURCE_STORAGE_KEY, value || "");
    } catch (error) {
    }
}

function normalizeExternalSourceUrl(value) {
    const input = (value || "").trim();
    if (!input) {
        return "";
    }

    if (input.includes("/api/events/calendar-feed")) {
        return input;
    }

    let normalized = input.replace(/\/+$/, "");
    normalized = normalized.replace(/\/admin$/i, "");
    return `${normalized}/api/events/calendar-feed`;
}

function getLocalAudienceId(id) {
    return `local:cohort:${id}`;
}

function getExternalAudienceId(sourceId, filterId) {
    return `external:${sourceId}:filter:${filterId}`;
}

function formatScheduleType(value) {
    switch (value) {
        case 'weekly':
            return 'Weekly';
        case 'daterange':
            return 'Date range';
        case 'runtime':
            return 'Runtime';
        default:
            return value || 'Unknown';
    }
}

export default class Calendar extends Component {
    static contextType = AuthContext;

    constructor(props, context) {
        super(props);
        this.authContext = context;

        this.state = {
            items: [],
            groups: [],
            audiences: [],
            cohorts: [],
            events: [],
            offers: [],
            notifications: [],
            externalItems: [],
            externalFilters: [],
            externalSourceUrl: getStoredExternalSourceUrl(),
            externalStatus: null,
            externalLoading: false,
            errors: [],
            loading: true,
            form: {
                audienceIds: [],
                isEvents: true,
                isOffers: true,
                isNotifications: false,
                isRemoteGameModes: true,
                isRemoteTournaments: true,
                isRemoteNotifications: false
            },
            visibleTimeStart: moment().add(-1, 'day').valueOf(),
            visibleTimeEnd: moment().add(45, 'day').valueOf()
        };

        this.timelineWheelElement = null;

        this.init = this.init.bind(this);
        this.initTimeline = this.initTimeline.bind(this);
        this.handleTimeChange = this.handleTimeChange.bind(this);
        this.renderTimelineItem = this.renderTimelineItem.bind(this);
        this.setTimelineWheelElement = this.setTimelineWheelElement.bind(this);
        this.zoomTimeline = this.zoomTimeline.bind(this);
        this.handleTimelineWheel = this.handleTimelineWheel.bind(this);
        this.handleZoomIn = this.handleZoomIn.bind(this);
        this.handleZoomOut = this.handleZoomOut.bind(this);
        this.handleExternalSourceUrlChange = this.handleExternalSourceUrlChange.bind(this);
        this.applyExternalSource = this.applyExternalSource.bind(this);
    }

    componentDidMount() {
        this.init();
    }

    componentWillUnmount() {
        if (this.timelineWheelElement) {
            this.timelineWheelElement.removeEventListener('wheel', this.handleTimelineWheel);
        }
    }

    async fetchJson(url) {
        const response = await fetcher(url);
        return response.json();
    }

    async fetchExternalJson(url) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), EXTERNAL_SOURCE_TIMEOUT_MS);

        try {
            const response = await fetch(url, {
                method: 'GET',
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } finally {
            clearTimeout(timeoutId);
        }
    }

    async init() {
        const errors = [];

        const [
            cohortsResult,
            eventsResult,
            offersResult,
            notificationsResult,
            eventRuntimeResult,
            notificationRuntimeResult,
            externalData
        ] = await Promise.all([
            Promise.allSettled([
                this.fetchJson(`${config.API()}collection/cohorts`),
                this.fetchJson(`${config.API()}collection/events`),
                this.fetchJson(`${config.API()}collection/offer-lines`),
                this.fetchJson(`${config.API()}collection/notifications`),
                this.fetchJson(`${config.API()}events/list`),
                this.fetchJson(`${config.API()}webplugin/get_notifications`)
            ]),
            this.loadExternalSource(this.state.externalSourceUrl)
        ]).then(([localResults, external]) => [
            localResults[0],
            localResults[1],
            localResults[2],
            localResults[3],
            localResults[4],
            localResults[5],
            external
        ]);

        const cohorts = cohortsResult.status === "fulfilled" ? cohortsResult.value : [];
        const events = eventsResult.status === "fulfilled" ? eventsResult.value : [];
        const offers = offersResult.status === "fulfilled" ? offersResult.value : [];
        const notifications = notificationsResult.status === "fulfilled" ? notificationsResult.value : [];

        if (cohortsResult.status !== "fulfilled") {
            errors.push("Failed to load cohorts");
        }
        if (eventsResult.status !== "fulfilled") {
            errors.push("Failed to load events");
        }
        if (offersResult.status !== "fulfilled") {
            errors.push("Failed to load offer lines");
        }
        if (notificationsResult.status !== "fulfilled") {
            errors.push("Failed to load notifications");
        }

        const eventRuntimeItems = eventRuntimeResult.status === "fulfilled"
            ? (eventRuntimeResult.value.items || eventRuntimeResult.value.data || [])
            : [];
        const notificationRuntimeItems = notificationRuntimeResult.status === "fulfilled"
            ? (notificationRuntimeResult.value.data || [])
            : [];

        this.applyRuntimeData(events, eventRuntimeItems);
        this.applyRuntimeData(notifications, notificationRuntimeItems);

        this.setState({
            cohorts,
            events,
            offers,
            notifications,
            externalItems: externalData.items,
            externalFilters: externalData.filters,
            externalStatus: externalData.status,
            errors,
            loading: false
        }, () => {
            this.syncAudiencesAndTimeline(
                this.state.form,
                this.state.visibleTimeStart,
                this.state.visibleTimeEnd
            );
        });
    }

    applyRuntimeData(sourceItems, runtimeItems) {
        if (!Array.isArray(sourceItems) || !Array.isArray(runtimeItems)) {
            return;
        }

        for (const item of sourceItems) {
            const runtimeItem = _.find(runtimeItems, x => x.id === item._id);
            if (!runtimeItem) {
                continue;
            }

            item.isActive = runtimeItem.isActive;
            item.startTime = runtimeItem.startTime;
            item.endTime = runtimeItem.endTime;
        }
    }

    normalizeExternalFilter(filter, sourceId, sourceTitle) {
        return {
            id: getExternalAudienceId(sourceId, filter.id),
            rawId: String(filter.id),
            title: `[${sourceTitle}] ${filter.title || filter.id}`,
            sourceId,
            sourceTitle,
            kind: 'filter'
        };
    }

    normalizeExternalItem(item, sourceId, sourceTitle) {
        return Object.assign({}, item, {
            id: String(item.id),
            title: item.title || String(item.id),
            sourceId,
            sourceTitle,
            calendarAudienceIds: Array.isArray(item.filters)
                ? item.filters.map(filterId => getExternalAudienceId(sourceId, String(filterId)))
                : []
        });
    }

    async loadExternalSource(inputUrl) {
        const normalizedUrl = normalizeExternalSourceUrl(inputUrl);
        if (!normalizedUrl) {
            return {
                items: [],
                filters: [],
                status: null
            };
        }

        try {
            const payload = await this.fetchExternalJson(normalizedUrl);
            const sourceId = _.get(payload, 'source.id') || 'external';
            const sourceTitle = _.get(payload, 'source.title') || sourceId;
            const filters = Array.isArray(payload.filters)
                ? payload.filters.map(filter => this.normalizeExternalFilter(filter, sourceId, sourceTitle))
                : [];
            const items = Array.isArray(payload.items)
                ? payload.items.map(item => this.normalizeExternalItem(item, sourceId, sourceTitle))
                : [];

            return {
                items,
                filters,
                status: `External source loaded: ${sourceTitle}`
            };
        } catch (error) {
            return {
                items: [],
                filters: [],
                status: `External source ignored: ${error.message || 'request failed'}`
            };
        }
    }

    buildAudienceOptions(cohorts, externalFilters) {
        const localAudiences = (cohorts || []).map(cohort => ({
            id: getLocalAudienceId(cohort._id),
            title: cohort.title,
            kind: 'cohort',
            source: 'local'
        }));

        return localAudiences.concat(externalFilters || []);
    }

    getItemAudienceIds(item) {
        if (Array.isArray(item.calendarAudienceIds)) {
            return item.calendarAudienceIds;
        }

        if (Array.isArray(item.cohortIds)) {
            return item.cohortIds.map(id => getLocalAudienceId(id));
        }

        return [];
    }

    passesAudienceFilter(item, selectedAudienceIds) {
        if (!selectedAudienceIds || selectedAudienceIds.length === 0) {
            return true;
        }

        const itemAudienceIds = this.getItemAudienceIds(item);
        if (itemAudienceIds.length === 0) {
            return true;
        }

        return _.intersection(itemAudienceIds, selectedAudienceIds).length > 0;
    }

    getAudienceTitles(item) {
        const audienceIds = this.getItemAudienceIds(item);
        if (!audienceIds.length) {
            return [];
        }

        const byId = _.keyBy(this.state.audiences, 'id');
        return audienceIds
            .map(id => byId[id])
            .filter(Boolean)
            .map(audience => audience.title);
    }

    buildItemTooltip(definition, item, occurrence, title) {
        const audienceTitles = this.getAudienceTitles(item);
        const lines = [
            `${definition.title}: ${title}`,
            `Time: ${occurrence.start.format('YYYY-MM-DD HH:mm')} - ${occurrence.end.format('YYYY-MM-DD HH:mm')}`,
            `Schedule: ${formatScheduleType(item.timeType)}`
        ];

        if (item.sourceTitle) {
            lines.push(`Source: ${item.sourceTitle}`);
        }

        if (typeof item.isActive === 'boolean' || typeof item.active === 'boolean') {
            lines.push(`Active: ${(item.isActive ?? item.active) ? 'Yes' : 'No'}`);
        }

        if (audienceTitles.length > 0) {
            lines.push(`Audience: ${audienceTitles.join(', ')}`);
        }

        return lines.join('\n');
    }

    buildItemTooltipLines(definition, item, occurrence, title) {
        return this.buildItemTooltip(definition, item, occurrence, title).split('\n');
    }

    getOccurrencesForRange(item, visibleStart, visibleEnd) {
        const occurrences = [];

        if (Array.isArray(item.dateRanges) && item.dateRanges.length > 0) {
            item.dateRanges.forEach((range, index) => {
                if (!Array.isArray(range) || range.length < 2) {
                    return;
                }

                const start = parseTimelineDate(range[0]);
                const end = parseTimelineDate(range[1]);
                if (!start || !end || end.isBefore(start)) {
                    return;
                }

                if (end.isBefore(visibleStart) || start.isAfter(visibleEnd)) {
                    return;
                }

                occurrences.push({
                    id: `range-${index}`,
                    start,
                    end
                });
            });
        }

        if (occurrences.length === 0 && Array.isArray(item.weekly) && item.weekly.length > 0) {
            let cursor = visibleStart.clone().startOf('day');
            const endCursor = visibleEnd.clone().endOf('day');

            while (cursor.isSameOrBefore(endCursor)) {
                const rule = item.weekly[cursor.day()];
                if (rule && rule.check) {
                    const time = typeof rule.time === "string" && rule.time.includes(":") ? rule.time : "00:00";
                    const parts = time.split(":");
                    const hours = parseInt(parts[0], 10) || 0;
                    const minutes = parseInt(parts[1], 10) || 0;
                    const durationMinutes = parseInt(rule.duration, 10) || 1440;

                    const start = cursor.clone().hour(hours).minute(minutes).second(0).millisecond(0);
                    const end = start.clone().add(durationMinutes, 'minutes');

                    if (!end.isBefore(visibleStart) && !start.isAfter(visibleEnd)) {
                        occurrences.push({
                            id: `weekly-${cursor.format('YYYY-MM-DD')}`,
                            start,
                            end
                        });
                    }
                }

                cursor.add(1, 'day');
            }
        }

        if (occurrences.length === 0) {
            const start = parseTimelineDate(item.startTime);
            const end = parseTimelineDate(item.endTime);

            if (start && end && !end.isBefore(start) && !end.isBefore(visibleStart) && !start.isAfter(visibleEnd)) {
                occurrences.push({
                    id: "runtime",
                    start,
                    end
                });
            }
        }

        return occurrences;
    }

    buildTimelineItems(definition, sourceItems, form, visibleStart, visibleEnd) {
        const groups = [];
        const items = [];

        for (const item of sourceItems) {
            if (!this.passesAudienceFilter(item, form.audienceIds)) {
                continue;
            }

            const occurrences = this.getOccurrencesForRange(item, visibleStart, visibleEnd);
            if (occurrences.length === 0) {
                continue;
            }

            const title = item.title || item.name || item._id || item.id;
            const groupId = `${definition.key}:${item._id || item.id}`;

            groups.push({
                id: groupId,
                title: `[${definition.title}] ${title}`
            });

            occurrences.forEach((occurrence, index) => {
                items.push({
                    id: `${groupId}:${occurrence.id}:${index}`,
                    group: groupId,
                    title,
                    tooltipLines: this.buildItemTooltipLines(definition, item, occurrence, title),
                    start_time: occurrence.start,
                    end_time: occurrence.end,
                    canMove: false,
                    canResize: false,
                    itemProps: {
                        title: this.buildItemTooltip(definition, item, occurrence, title),
                        style: {
                            background: definition.color,
                            borderColor: "#ccc",
                            color: "#fff"
                        }
                    }
                });
            });
        }

        return { groups, items };
    }

    syncAudiencesAndTimeline(form, visibleTimeStart, visibleTimeEnd) {
        const audiences = this.buildAudienceOptions(this.state.cohorts, this.state.externalFilters);
        const audienceIdsSet = new Set(audiences.map(audience => audience.id));
        const nextForm = Object.assign({}, form, {
            audienceIds: (form.audienceIds || []).filter(id => audienceIdsSet.has(id))
        });

        this.setState({
            audiences
        }, () => {
            this.initTimeline(nextForm, visibleTimeStart, visibleTimeEnd);
        });
    }

    initTimeline(form, visibleTimeStart, visibleTimeEnd) {
        const visibleStart = moment(visibleTimeStart);
        const visibleEnd = moment(visibleTimeEnd);

        const enabledDefinitions = [
            form.isEvents ? {
                key: "event",
                title: "Event",
                color: EVENT_COLOR,
                items: this.state.events
            } : null,
            form.isOffers ? {
                key: "offer",
                title: "Offer",
                color: OFFER_COLOR,
                items: this.state.offers
            } : null,
            form.isNotifications ? {
                key: "notification",
                title: "Notification",
                color: NOTIFICATION_COLOR,
                items: this.state.notifications
            } : null,
            form.isRemoteGameModes ? {
                key: "remote-game-mode",
                title: "Bingo Game Mode",
                color: REMOTE_GAME_MODE_COLOR,
                items: this.state.externalItems.filter(item => item.sourceType === 'gameMode')
            } : null,
            form.isRemoteTournaments ? {
                key: "remote-tournament",
                title: "Bingo Tournament",
                color: REMOTE_TOURNAMENT_COLOR,
                items: this.state.externalItems.filter(item => item.sourceType === 'tournament')
            } : null,
            form.isRemoteNotifications ? {
                key: "remote-notification",
                title: "Bingo Notification",
                color: REMOTE_NOTIFICATION_COLOR,
                items: this.state.externalItems.filter(item => item.sourceType === 'notification')
            } : null
        ].filter(Boolean);

        let groups = [];
        let items = [];

        enabledDefinitions.forEach(definition => {
            const timelineData = this.buildTimelineItems(
                definition,
                definition.items,
                form,
                visibleStart,
                visibleEnd
            );
            groups = groups.concat(timelineData.groups);
            items = items.concat(timelineData.items);
        });

        groups = _.sortBy(groups, x => x.title.toLowerCase());
        items = _.sortBy(items, x => x.start_time.valueOf());

        this.setState({
            form,
            groups,
            items,
            visibleTimeStart,
            visibleTimeEnd
        });
    }

    handleTimeChange(visibleTimeStart, visibleTimeEnd, updateScrollCanvas) {
        updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
        this.initTimeline(this.state.form, visibleTimeStart, visibleTimeEnd);
    }

    setTimelineWheelElement(element) {
        if (this.timelineWheelElement === element) {
            return;
        }

        if (this.timelineWheelElement) {
            this.timelineWheelElement.removeEventListener('wheel', this.handleTimelineWheel);
        }

        this.timelineWheelElement = element;

        if (this.timelineWheelElement) {
            this.timelineWheelElement.addEventListener('wheel', this.handleTimelineWheel, { passive: false });
        }
    }

    zoomTimeline(scale, offset) {
        const oldZoom = this.state.visibleTimeEnd - this.state.visibleTimeStart;
        const newZoom = Math.min(
            Math.max(Math.round(oldZoom * scale), TIMELINE_MIN_ZOOM_MS),
            TIMELINE_MAX_ZOOM_MS
        );
        const visibleTimeStart = Math.round(
            this.state.visibleTimeStart + (oldZoom - newZoom) * offset
        );

        this.initTimeline(
            this.state.form,
            visibleTimeStart,
            visibleTimeStart + newZoom
        );
    }

    renderTimelineItem({
        item,
        itemContext,
        getItemProps,
        getResizeProps
    }) {
        const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();

        return (
            <div {...getItemProps(item.itemProps)}>
                {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}

                <div
                    className="rct-item-content"
                    style={{ maxHeight: `${itemContext.dimensions.height}` }}
                >
                    {itemContext.title}
                </div>

                <div className="calendar-item-tooltip">
                    {(item.tooltipLines || []).map((line, index) => (
                        <div key={`tooltip-line-${item.id}-${index}`}>{line}</div>
                    ))}
                </div>

                {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
            </div>
        );
    }

    handleTimelineWheel(event) {
        if (!this.timelineWheelElement) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const rect = this.timelineWheelElement.getBoundingClientRect();
        const width = rect.width || this.timelineWheelElement.clientWidth;
        if (!width) {
            return;
        }

        const limitedDelta = Math.max(-120, Math.min(120, event.deltaY || 0));
        const scale = 1 + limitedDelta / TIMELINE_WHEEL_ZOOM_DIVISOR;
        const offset = Math.max(0, Math.min(1, (event.clientX - rect.left) / width));
        this.zoomTimeline(scale, offset);
    }

    handleZoomIn() {
        this.zoomTimeline(0.85, 0.5);
    }

    handleZoomOut() {
        this.zoomTimeline(1.18, 0.5);
    }

    handleExternalSourceUrlChange(event) {
        this.setState({
            externalSourceUrl: event.target.value
        });
    }

    async applyExternalSource(event) {
        if (event) {
            event.preventDefault();
        }

        const externalSourceUrl = (this.state.externalSourceUrl || "").trim();
        setStoredExternalSourceUrl(externalSourceUrl);

        this.setState({
            externalLoading: true
        });

        const externalData = await this.loadExternalSource(externalSourceUrl);

        this.setState({
            externalItems: externalData.items,
            externalFilters: externalData.filters,
            externalStatus: externalData.status,
            externalLoading: false
        }, () => {
            this.syncAudiencesAndTimeline(
                this.state.form,
                this.state.visibleTimeStart,
                this.state.visibleTimeEnd
            );
        });
    }

    render() {
        if (this.state.redirect) {
            return <Redirect push to={this.state.redirect} />;
        }

        return (
            <div>
                <div className="card shadow w100">
                    <div className="card-header py-3">
                        <h2 className="m-0 font-weight-bold l"> Calendar</h2>
                    </div>
                    <div className="card-body small">
                        {this.state.errors.length > 0 && (
                            <div className="alert alert-warning">
                                {this.state.errors.map((error, index) => (
                                    <div key={`calendar-error-${index}`}>{error}</div>
                                ))}
                            </div>
                        )}

                        <Formik initialValues={this.state.form} enableReinitialize={true}
                                onSubmit={values => this.initTimeline(values, this.state.visibleTimeStart, this.state.visibleTimeEnd)}>
                            {({
                                  values,
                                  handleSubmit,
                                  handleChange,
                                  setFieldValue
                               }) => {
                                return (
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group row">
                                        <div className="col-2 col-form-label">
                                            <div>
                                                <Link to={`/cohorts`}>Audiences (cohorts + external filters)</Link>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isEvents" className="col-6 col-form-label">Events</label>
                                                <div className="col-6 pt-2">
                                                    <input id="isEvents" type="checkbox" name="isEvents"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isEvents}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isOffers" className="col-6 col-form-label">Offers</label>
                                                <div className="col-6">
                                                    <input id="isOffers" type="checkbox" name="isOffers"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isOffers}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isNotifications" className="col-6 col-form-label">Notifications</label>
                                                <div className="col-6">
                                                    <input id="isNotifications" type="checkbox" name="isNotifications"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isNotifications}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isRemoteGameModes" className="col-6 col-form-label">Bingo Modes</label>
                                                <div className="col-6">
                                                    <input id="isRemoteGameModes" type="checkbox" name="isRemoteGameModes"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isRemoteGameModes}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isRemoteTournaments" className="col-6 col-form-label">Bingo Tournaments</label>
                                                <div className="col-6">
                                                    <input id="isRemoteTournaments" type="checkbox" name="isRemoteTournaments"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isRemoteTournaments}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label htmlFor="isRemoteNotifications" className="col-6 col-form-label">Bingo Notifications</label>
                                                <div className="col-6">
                                                    <input id="isRemoteNotifications" type="checkbox" name="isRemoteNotifications"
                                                           className="form-check-input ml-3"
                                                           onChange={handleChange}
                                                           checked={values.isRemoteNotifications}/>
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <div className="col-12">
                                                    <button type="submit" className="btn btn-icon btn-primary mt-3">Filter</button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-5">
                                            <Field component="select"
                                                   className="form-control cohorts"
                                                   name="audienceIds"
                                                   onChange={evt =>
                                                       setFieldValue(
                                                           "audienceIds",
                                                           [].slice.call(evt.target.selectedOptions).map(option => option.value)
                                                       )
                                                   }
                                                   multiple={true}>
                                                {this.state.audiences.map(audience => (
                                                    <option key={`audience-${audience.id}`} value={audience.id}>
                                                        {audience.title}
                                                    </option>
                                                ))}
                                            </Field>
                                        </div>
                                        <div className="col-5">
                                        </div>
                                    </div>
                                </form>
                                );
                               }}
                        </Formik>

                        {this.state.loading && <div>Loading...</div>}

                        {!this.state.loading && this.state.items.length === 0 && (
                            <div className="alert alert-info">No scheduled liveops items found for the selected filters and time range.</div>
                        )}

                        {!this.state.loading && this.state.items.length > 0 && (
                            <div>
                                <div className="d-flex justify-content-end mb-2">
                                    <div className="btn-group btn-group-sm" role="group" aria-label="Calendar zoom controls">
                                        <button type="button" className="btn btn-outline-secondary calendar-zoom-button" onClick={this.handleZoomOut}>-</button>
                                        <button type="button" className="btn btn-outline-secondary calendar-zoom-button" onClick={this.handleZoomIn}>+</button>
                                    </div>
                                </div>
                                <div ref={this.setTimelineWheelElement} className="calendar-timeline-shell">
                                <Timeline
                                    groups={this.state.groups}
                                    items={this.state.items}
                                    itemRenderer={this.renderTimelineItem}
                                    sidebarWidth={250}
                                    lineHeight={40}
                                    visibleTimeStart={this.state.visibleTimeStart}
                                    visibleTimeEnd={this.state.visibleTimeEnd}
                                    onTimeChange={this.handleTimeChange}
                                >
                                    <TimelineHeaders>
                                        <SidebarHeader/>
                                        <DateHeader unit="primaryHeader" style={{ height: 50 }}/>
                                        <DateHeader unit="day" labelFormat="DD" style={{ height: 50 }}/>
                                    </TimelineHeaders>
                                </Timeline>
                                </div>
                            </div>
                        )}

                        <div className="card mt-4">
                            <div className="card-header py-2">
                                <strong>External Source</strong>
                            </div>
                            <div className="card-body">
                                <div className="form-group row mb-2">
                                    <label className="col-sm-2 col-form-label">URL</label>
                                    <div className="col-sm-8">
                                        <input
                                            className="form-control"
                                            value={this.state.externalSourceUrl}
                                            onChange={this.handleExternalSourceUrlChange}
                                            placeholder="http://localhost:3000/api/events/calendar-feed or http://localhost:3000/admin/"
                                        />
                                    </div>
                                    <div className="col-sm-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-block"
                                            onClick={this.applyExternalSource}
                                            disabled={this.state.externalLoading}
                                        >
                                            {this.state.externalLoading ? "Loading..." : "Apply"}
                                        </button>
                                    </div>
                                </div>
                                <div className="text-muted">
                                    Empty value disables the external source. Unreachable source is ignored.
                                </div>
                                {this.state.externalStatus && (
                                    <div className="mt-2 text-muted">{this.state.externalStatus}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
